/**
 * Input sanitization utilities for enforcing Latin-1 (ISO-8859-1) character encoding.
 * Latin-1 includes characters from code points 0x00 to 0xFF (0-255).
 */

/**
 * Map of common Unicode characters to their Latin-1 equivalents.
 * This helps preserve meaning when users paste text from Word, emails, etc.
 */
const UNICODE_TO_LATIN1_MAP: Record<string, string> = {
    // Curly/smart quotes to straight quotes
    '\u2018': "'", // Left single quotation mark
    '\u2019': "'", // Right single quotation mark
    '\u201A': "'", // Single low-9 quotation mark
    '\u201B': "'", // Single high-reversed-9 quotation mark
    '\u201C': '"', // Left double quotation mark
    '\u201D': '"', // Right double quotation mark
    '\u201E': '"', // Double low-9 quotation mark
    '\u201F': '"', // Double high-reversed-9 quotation mark
    '\u2032': "'", // Prime (feet)
    '\u2033': '"', // Double prime (inches)
    '\u2039': "'", // Single left-pointing angle quotation mark
    '\u203A': "'", // Single right-pointing angle quotation mark
    // Note: \u00AB («) and \u00BB (») are already Latin-1 and preserved as-is

    // Dashes and hyphens
    '\u2010': '-', // Hyphen
    '\u2011': '-', // Non-breaking hyphen
    '\u2012': '-', // Figure dash
    '\u2013': '-', // En dash
    '\u2014': '-', // Em dash
    '\u2015': '-', // Horizontal bar
    '\u2212': '-', // Minus sign

    // Spaces
    // Note: \u00A0 (non-breaking space) is already Latin-1 and preserved as-is
    '\u2000': ' ', // En quad
    '\u2001': ' ', // Em quad
    '\u2002': ' ', // En space
    '\u2003': ' ', // Em space
    '\u2004': ' ', // Three-per-em space
    '\u2005': ' ', // Four-per-em space
    '\u2006': ' ', // Six-per-em space
    '\u2007': ' ', // Figure space
    '\u2008': ' ', // Punctuation space
    '\u2009': ' ', // Thin space
    '\u200A': ' ', // Hair space
    '\u200B': '',  // Zero-width space (remove)
    '\u202F': ' ', // Narrow no-break space
    '\u205F': ' ', // Medium mathematical space
    '\u3000': ' ', // Ideographic space
    '\uFEFF': '',  // Zero-width no-break space / BOM (remove)

    // Ellipsis
    '\u2026': '...', // Horizontal ellipsis

    // Bullets and list markers
    '\u2022': '*', // Bullet
    '\u2023': '>', // Triangular bullet
    '\u2043': '-', // Hyphen bullet
    '\u25AA': '*', // Black small square
    '\u25CF': '*', // Black circle
    '\u25E6': 'o', // White bullet

    // Other common replacements
    '\u2122': '(TM)', // Trade mark sign
    // Note: \u00AE (®) and \u00A9 (©) are already Latin-1 and preserved as-is
    '\u2020': '+',    // Dagger
    '\u2021': '++',   // Double dagger
    '\u2030': '%o',   // Per mille sign
    '\u2190': '<-',   // Leftwards arrow
    '\u2192': '->',   // Rightwards arrow
    '\u2194': '<->',  // Left right arrow
    '\u21D2': '=>',   // Rightwards double arrow
    '\u2260': '!=',   // Not equal to
    '\u2264': '<=',   // Less-than or equal to
    '\u2265': '>=',   // Greater-than or equal to
    '\u221E': 'inf',  // Infinity
    '\u2248': '~=',   // Almost equal to
};

/**
 * Result of sanitizing a string to Latin-1.
 */
export interface SanitizeToLatin1Result {
    /** The sanitized string containing only Latin-1 characters */
    value: string;
    /** Whether any characters were modified or removed */
    wasModified: boolean;
    /** Whether any characters were removed (not just replaced) */
    hadRemovedChars: boolean;
}

/**
 * Result of sanitizing a string to Latin-1 with cursor position tracking.
 */
export interface SanitizeToLatin1WithCursorResult extends SanitizeToLatin1Result {
    /** The new cursor position after sanitization */
    newCursorPosition: number;
}

/**
 * Checks if a character is within the Latin-1 (ISO-8859-1) range.
 * Latin-1 includes code points 0x00-0xFF (0-255).
 * 
 * @param char - Single character to check
 * @returns true if the character is Latin-1 compatible
 */
export const isLatin1Char = (char: string): boolean => {
    const code = char.charCodeAt(0);
    return code <= 0xFF;
};

/**
 * Checks if a string contains only Latin-1 (ISO-8859-1) characters.
 * Uses for...of to properly handle Unicode surrogate pairs.
 * 
 * @param str - String to check
 * @returns true if all characters are Latin-1 compatible
 */
export const isLatin1String = (str: string): boolean => {
    if (!str) return true;
    // Use for...of to properly iterate over code points (handles surrogate pairs)
    for (const char of str) {
        if (!isLatin1Char(char)) {
            return false;
        }
    }
    return true;
};

/**
 * Sanitizes a string to only contain Latin-1 (ISO-8859-1) characters.
 * Returns detailed information about what was changed.
 * Uses for...of to properly handle Unicode surrogate pairs (e.g., emojis).
 * 
 * This function:
 * 1. Replaces common Unicode characters with Latin-1 equivalents (e.g., curly quotes → straight quotes)
 * 2. Removes any remaining non-Latin-1 characters
 * 
 * @param str - String to sanitize
 * @returns Object containing sanitized string and modification flags
 */
export const sanitizeToLatin1WithInfo = (str: string): SanitizeToLatin1Result => {
    if (!str) return { value: str, wasModified: false, hadRemovedChars: false };

    let result = '';
    let wasModified = false;
    let hadRemovedChars = false;
    
    // Use for...of to properly iterate over code points (handles surrogate pairs like emojis)
    for (const char of str) {
        // Check if there's a mapping for this character
        if (UNICODE_TO_LATIN1_MAP[char] !== undefined) {
            const replacement = UNICODE_TO_LATIN1_MAP[char];
            result += replacement;
            // Only mark as modified if the replacement differs from original
            if (replacement !== char) {
                wasModified = true;
            }
        } else if (isLatin1Char(char)) {
            // Character is already Latin-1, keep it
            result += char;
        } else {
            // Non-Latin-1 character without a mapping - remove it
            wasModified = true;
            hadRemovedChars = true;
        }
    }
    
    return { value: result, wasModified, hadRemovedChars };
};

/**
 * Sanitizes a string to only contain Latin-1 (ISO-8859-1) characters,
 * with proper cursor position tracking.
 * Uses for...of to properly handle Unicode surrogate pairs (e.g., emojis).
 * 
 * This function correctly handles cursor position when characters are:
 * - Replaced with longer strings (e.g., '™' → '(TM)')
 * - Replaced with shorter strings
 * - Removed entirely
 * - Surrogate pairs (emojis) that take 2 code units but represent 1 character
 * 
 * @param str - String to sanitize
 * @param cursorPosition - Current cursor position in the original string (in code units)
 * @returns Object containing sanitized string, modification flags, and new cursor position
 */
export const sanitizeToLatin1WithCursor = (str: string, cursorPosition: number): SanitizeToLatin1WithCursorResult => {
    if (!str) return { value: str, wasModified: false, hadRemovedChars: false, newCursorPosition: cursorPosition };

    let result = '';
    let wasModified = false;
    let hadRemovedChars = false;
    let newCursorPosition = 0;
    let codeUnitIndex = 0; // Track position in code units (for cursor comparison)
    
    // Use for...of to properly iterate over code points (handles surrogate pairs like emojis)
    for (const char of str) {
        const charCodeUnits = char.length; // 1 for BMP chars, 2 for surrogate pairs
        let replacement: string;
        
        // Check if there's a mapping for this character
        if (UNICODE_TO_LATIN1_MAP[char] !== undefined) {
            replacement = UNICODE_TO_LATIN1_MAP[char];
            // Only mark as modified if the replacement differs from original
            if (replacement !== char) {
                wasModified = true;
                if (replacement.length === 0) {
                    hadRemovedChars = true;
                }
            }
        } else if (isLatin1Char(char)) {
            // Character is already Latin-1, keep it
            replacement = char;
        } else {
            // Non-Latin-1 character without a mapping - remove it
            replacement = '';
            wasModified = true;
            hadRemovedChars = true;
        }
        
        result += replacement;
        
        // Update cursor position: if we're still before the original cursor position,
        // track where the cursor should be in the new string.
        // Note: cursorPosition is in code units, so we compare with codeUnitIndex
        if (codeUnitIndex < cursorPosition) {
            // If the cursor is within this character (for surrogate pairs),
            // add the full replacement length
            if (codeUnitIndex + charCodeUnits <= cursorPosition) {
                newCursorPosition += replacement.length;
            } else {
                // Cursor is in the middle of a surrogate pair - treat as after the character
                newCursorPosition += replacement.length;
            }
        }
        
        codeUnitIndex += charCodeUnits;
    }
    
    // If cursor was at or past end of string, put it at end of result
    if (cursorPosition >= str.length) {
        newCursorPosition = result.length;
    }
    
    return { value: result, wasModified, hadRemovedChars, newCursorPosition };
};

/**
 * Sanitizes a string to only contain Latin-1 (ISO-8859-1) characters.
 * Simple version that just returns the sanitized string.
 * 
 * @param str - String to sanitize
 * @returns Sanitized string containing only Latin-1 characters
 */
export const sanitizeToLatin1 = (str: string): string => {
    return sanitizeToLatin1WithInfo(str).value;
};

/** Default validation message for non-Latin-1 characters */
export const LATIN1_VALIDATION_MESSAGE = 'Some characters are not supported and were removed';

/**
 * Result of validating a string for Latin-1 compatibility.
 */
export interface Latin1ValidationResult {
    /** Whether the string contains only Latin-1 characters */
    isValid: boolean;
    /** Error message describing the validation failure (if any) */
    errorMessage?: string;
    /** Array of unique invalid characters found in the string */
    invalidChars: string[];
}

/**
 * Validates a string to check if it contains only Latin-1 (ISO-8859-1) characters.
 * Does not modify the string - only checks for validity.
 * 
 * Returns detailed information about any invalid characters found, including:
 * - Validation status
 * - Formatted error message listing the invalid characters
 * - Array of unique invalid characters
 * 
 * @param str - String to validate
 * @returns Validation result with error message and invalid characters
 */
export const validateLatin1String = (str: string): Latin1ValidationResult => {
    if (!str) {
        return { isValid: true, invalidChars: [] };
    }

    const invalidCharsSet = new Set<string>();
    const invalidCharsInOrder: string[] = [];
    
    // Use for...of to properly iterate over code points (handles surrogate pairs like emojis)
    for (const char of str) {
        // Skip characters that have a Latin-1 mapping (these are "acceptable" substitutions)
        if (UNICODE_TO_LATIN1_MAP[char] !== undefined) {
            continue;
        }
        
        // Check if character is not Latin-1
        if (!isLatin1Char(char)) {
            // Only add if we haven't seen this character before
            if (!invalidCharsSet.has(char)) {
                invalidCharsSet.add(char);
                invalidCharsInOrder.push(char);
            }
        }
    }
    
    if (invalidCharsInOrder.length === 0) {
        return { isValid: true, invalidChars: [] };
    }
    
    // Format error message with the invalid characters
    const maxCharsToShow = 10;
    let charsList: string;
    
    if (invalidCharsInOrder.length <= maxCharsToShow) {
        charsList = invalidCharsInOrder.join(', ');
    } else {
        const visibleChars = invalidCharsInOrder.slice(0, maxCharsToShow).join(', ');
        const remainingCount = invalidCharsInOrder.length - maxCharsToShow;
        charsList = `${visibleChars}... and ${remainingCount} more`;
    }
    
    const errorMessage = `String contains invalid characters: ${charsList}`;
    
    return {
        isValid: false,
        errorMessage,
        invalidChars: invalidCharsInOrder,
    };
};

/**
 * Result of applying Latin-1 sanitization to an input element.
 */
export interface ApplyLatin1SanitizationResult {
    /** The sanitized value */
    sanitizedValue: string;
    /** Whether any characters were modified or removed */
    wasModified: boolean;
}

/**
 * Applies Latin-1 sanitization to an input or textarea element,
 * updating the element's value and preserving cursor position.
 * 
 * This is a helper function that centralizes the logic for:
 * 1. Getting the current cursor position
 * 2. Sanitizing the value with cursor tracking
 * 3. Updating the DOM element's value
 * 4. Restoring the cursor position
 * 
 * @param element - The input or textarea element to sanitize
 * @returns Object with sanitized value and whether modification occurred
 */
export const applyLatin1Sanitization = <T extends HTMLInputElement | HTMLTextAreaElement>(
    element: T
): ApplyLatin1SanitizationResult => {
    const cursorPosition = element.selectionStart ?? element.value.length;
    
    const { value: sanitizedValue, wasModified, newCursorPosition } = sanitizeToLatin1WithCursor(
        element.value,
        cursorPosition
    );
    
    if (wasModified) {
        // Update the element's value
        element.value = sanitizedValue;
        
        // Restore cursor position using the correctly calculated position
        element.setSelectionRange(newCursorPosition, newCursorPosition);
    }
    
    return { sanitizedValue, wasModified };
};

/**
 * Creates an onChange handler that sanitizes input to Latin-1 characters.
 * Useful for wrapping existing onChange handlers.
 * 
 * @param originalHandler - Original onChange handler (optional)
 * @returns New onChange handler that sanitizes the value before calling the original
 */
export const createLatin1OnChangeHandler = <T extends HTMLInputElement | HTMLTextAreaElement>(
    originalHandler?: (e: React.ChangeEvent<T>) => void
): ((e: React.ChangeEvent<T>) => void) => {
    return (e: React.ChangeEvent<T>) => {
        applyLatin1Sanitization(e.target);
        
        // Call the original handler if provided
        originalHandler?.(e);
    };
};
